import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Users, 
  Bell, 
  CheckCircle, 
  Loader2,
  AlertCircle,
  Heart,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/SupabaseAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvitoPage = () => {
  const { inviteToken } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);

  // Fetch invite info
  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await fetch(`${API_URL}/api/destinatari/invite/${inviteToken}`);
        const data = await res.json();
        
        if (data.success && data.valid) {
          setInviteInfo(data);
        } else {
          setError(data.error || 'Invito non valido');
        }
      } catch (err) {
        setError('Errore nel caricamento dell\'invito');
      } finally {
        setLoading(false);
      }
    };

    if (inviteToken) {
      fetchInvite();
    }
  }, [inviteToken]);

  // Accept invite
  const handleAccept = async () => {
    if (!user) {
      // Redirect to login with return URL
      toast.info('Accedi o registrati per accettare l\'invito');
      navigate(`/login?redirect=/invito/${inviteToken}`);
      return;
    }

    setAccepting(true);
    
    try {
      // Accept the invite (without fcm_token)
      const res = await fetch(`${API_URL}/api/destinatari/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invite_token: inviteToken,
          user_id: user.id
        })
      });

      const data = await res.json();

      if (data.success) {
        setAccepted(true);
        toast.success('Invito accettato! Ora riceverai le notifiche.');
        
        // Register for OneSignal push notifications
        try {
          if (window.OneSignalDeferred) {
            window.OneSignalDeferred.push(async function(OneSignal) {
              try {
                // Request notification permission
                await OneSignal.Notifications.requestPermission();
                
                const permission = await OneSignal.Notifications.permission;
                if (!permission) {
                  console.log('[OneSignal] Permission not granted');
                  return;
                }
                
                // Login with famiglia_id as external_id
                if (data.famiglia_id) {
                  await OneSignal.login(data.famiglia_id);
                }
                
                // Opt in and get subscription id
                await OneSignal.User.PushSubscription.optIn();
                
                const optedIn = await OneSignal.User.PushSubscription.optedIn;
                let playerId = await OneSignal.User.PushSubscription.id;
                
                // If id not immediately available, wait for change event
                if (optedIn && playerId && data.destinatario_id) {
                  // Register destinatario with OneSignal player_id
                  await fetch(`${API_URL}/api/notifications/register-destinatario?destinatario_id=${data.destinatario_id}&player_id=${playerId}&platform=web`, {
                    method: 'POST'
                  });
                  console.log('[OneSignal] Destinatario registered with player_id');
                } else if (data.destinatario_id) {
                  // Set up change listener for when id becomes available
                  const handleChange = async (event) => {
                    const id = event?.current?.id;
                    const opted = event?.current?.optedIn;
                    if (opted && id) {
                      await fetch(`${API_URL}/api/notifications/register-destinatario?destinatario_id=${data.destinatario_id}&player_id=${id}&platform=web`, {
                        method: 'POST'
                      });
                      console.log('[OneSignal] Destinatario registered via change event');
                      OneSignal.User.PushSubscription.removeEventListener('change', handleChange);
                    }
                  };
                  OneSignal.User.PushSubscription.addEventListener('change', handleChange);
                }
              } catch (osErr) {
                console.log('[OneSignal] Registration error:', osErr);
              }
            });
          }
        } catch (pushErr) {
          console.log('[OneSignal] Setup error:', pushErr);
        }
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        toast.error(data.error || 'Errore nell\'accettazione');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    } finally {
      setAccepting(false);
    }
  };

  // Loading state
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F0F9F1] to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#4A7C59] mx-auto mb-4" />
          <p className="text-[#64748B]">Caricamento invito...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F0F9F1] to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-[#2C3E50] mb-2">Invito non valido</h1>
          <p className="text-[#64748B] mb-6">{error}</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#4A7C59] hover:bg-[#3D6A4B] text-white"
          >
            Torna alla Home
          </Button>
        </div>
      </div>
    );
  }

  // Success state
  if (accepted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F0F9F1] to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-[#2C3E50] mb-2">Invito Accettato!</h1>
          <p className="text-[#64748B] mb-2">
            Ora sei collegato alla famiglia di <strong>{inviteInfo?.assistito_nome}</strong>
          </p>
          <p className="text-sm text-[#64748B] mb-6">
            Riceverai notifiche su tutte le attivita registrate.
          </p>
          <div className="flex items-center justify-center gap-2 text-[#4A7C59]">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Reindirizzamento alla dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  // Invite info display
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0F9F1] to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-[#4A7C59] to-[#6B9B7A] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Users className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">Sei stato invitato!</h1>
          <p className="text-[#64748B] mt-2">
            {inviteInfo?.destinatario_nome}, sei stato invitato a ricevere le notifiche di CuraCheck
          </p>
        </div>

        {/* Family Info Card */}
        <div className="bg-[#F0F9F1] rounded-xl p-5 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
              <Heart className="w-7 h-7 text-[#E74C3C]" />
            </div>
            <div>
              <p className="font-bold text-[#2C3E50] text-lg">{inviteInfo?.assistito_nome}</p>
              <p className="text-sm text-[#64748B]">Famiglia {inviteInfo?.famiglia_nome}</p>
            </div>
          </div>
          
          <div className="border-t border-[#E5E7EB] pt-4 space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <Bell className="w-5 h-5 text-[#4A7C59]" />
              <span className="text-[#2C3E50]">Riceverai notifiche su tutte le attivita</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Shield className="w-5 h-5 text-[#4A7C59]" />
              <span className="text-[#2C3E50]">Avrai accesso alla panoramica completa</span>
            </div>
          </div>
        </div>

        {/* What you will receive */}
        <div className="mb-6">
          <p className="text-sm font-semibold text-[#2C3E50] mb-3">Cosa riceverai:</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {['Terapie somministrate', 'Pasti registrati', 'Attivita svolte', 'Emergenze SOS', 'Umore giornaliero', 'Note importanti'].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-[#64748B]">
                <CheckCircle className="w-4 h-4 text-[#4A7C59]" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Accept Button */}
        <Button
          onClick={handleAccept}
          disabled={accepting}
          className="w-full h-14 bg-gradient-to-r from-[#4A7C59] to-[#6B9B7A] hover:from-[#3D6A4B] hover:to-[#5A8A6A] text-white text-lg font-semibold rounded-xl shadow-lg"
          data-testid="accept-invite-btn"
        >
          {accepting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Accettazione...
            </>
          ) : user ? (
            'Accetta Invito'
          ) : (
            'Accedi per Accettare'
          )}
        </Button>

        {!user && (
          <p className="text-center text-sm text-[#64748B] mt-4">
            Non hai un account?{' '}
            <button 
              onClick={() => navigate(`/register?redirect=/invito/${inviteToken}`)}
              className="text-[#4A7C59] font-semibold hover:underline"
            >
              Registrati gratis
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

export default InvitoPage;
